/**
 * TYPES
 */
import {_aggregates} from 'src/aggregates/index.d';
import {IPreferencesAddedAutoMessage} from 'src/aggregates/preferences/events.d';
import {IPreferencesEmailPageSizeSet} from 'src/aggregates/preferences/events.d';
import {IPreferencesFeedbackSet} from 'src/aggregates/preferences/events.d';
import {IPreferencesRemovedAutoMessage} from 'src/aggregates/preferences/events.d';
import {IPreferencesSetCurrentAutoMessage} from 'src/aggregates/preferences/events.d';
import {IPreferencesStarted} from 'src/aggregates/preferences/events.d';
import {IPreferencesTagColorSet} from 'src/aggregates/preferences/events.d';
import {IPreferencesSwitchedMuteNotifications} from 'src/aggregates/preferences/events.d';
import {IPreferencesSwitchedPaused} from 'src/aggregates/preferences/events.d';
import {IPreferencesSwitchedWalletReminder} from 'src/aggregates/preferences/events.d';
import {IPreferencesTourDisabled} from 'src/aggregates/preferences/events.d';
import {types} from 'src/aggregates/preferences/events.d';


/**
 * CODE
 */

/**
 * I create a preferences added auto message event.
 *
 * :param message: the message text to add
 * :param messageId: the message id to save
 * :param user: logged user id
 *
 * :returns: preferences added auto message event
 */
function preferencesAddedAutoMessage(
    message: string,
    messageId: number,
    user: number,
): IPreferencesAddedAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_ADDED_AUTO_MESSAGE,
        _target: user,
        message,
        messageId,
        type: types.PREFERENCES_ADDED_AUTO_MESSAGE,
    };
}


/**
 * I create an email page size set event.
 *
 * :param size: the size of email page
 * :param user: logged user id
 *
 * :returns: email page size set event
 */
function preferencesEmailPageSizeSet(
    size: number,
    user: number,
): IPreferencesEmailPageSizeSet
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_EMAIL_PAGE_SIZE_SET,
        _target: user,
        size,
        type: types.PREFERENCES_EMAIL_PAGE_SIZE_SET,
    };
}


/**
 * I create a user feedback set event.
 *
 * :param feedback: timestamp of the last user's feedback
 *
 * :returns: user feedback set event
 */
function preferencesFeedbackSet (
    feedback: number,
    user: number,
): IPreferencesFeedbackSet
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_FEEDBACK_SET,
        _target: user,
        feedback,
        type: types.PREFERENCES_FEEDBACK_SET,
    };
}


/**
 * I create a preferences removed auto message event.
 *
 * :param message: the message id to remove
 * :param user: logged user id
 *
 * :returns: preferences removed auto message event
 */
function preferencesRemovedAutoMessage(
    message: number,
    user: number,
): IPreferencesRemovedAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_REMOVED_AUTO_MESSAGE,
        _target: user,
        message,
        type: types.PREFERENCES_REMOVED_AUTO_MESSAGE,
    };
}


/**
 * I create a preferences set current auto message event.
 *
 * :param message: the message id to use
 * :param user: logged user id
 *
 * :returns: preferences set current auto message event
 */
function preferencesSetCurrentAutoMessage(
    message: number,
    user: number,
): IPreferencesSetCurrentAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_SET_CURRENT_AUTO_MESSAGE,
        _target: user,
        message,
        type: types.PREFERENCES_SET_CURRENT_AUTO_MESSAGE,
    };
}


/**
 * I create a preferences started event.
 *
 * :param user: logged user id
 *
 * :returns: preferences started event
 */
function preferencesStarted(user: number): IPreferencesStarted
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_STARTED,
        _target: user,
        type: types.PREFERENCES_STARTED,
    };
}


/**
 * I create a preferences switched mute notifications event.
 *
 * :param user: logged user id
 * :param muteNotifications: switched mute notifications state
 *
 * :returns: preferences switched mute notifications event
 */
function preferencesSwitchedMuteNotifications(
    user: number,
    muteNotifications: boolean,
): IPreferencesSwitchedMuteNotifications
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_SWITCHED_MUTE_NOTIFICATIONS,
        _target: user,
        muteNotifications,
        type: types.PREFERENCES_SWITCHED_MUTE_NOTIFICATIONS,
    };
}


/**
 * I create a preferences switched paused event.
 *
 * :param user: logged user id
 * :param paused: switched paused state
 *
 * :returns: preferences switched paused event
 */
 function preferencesSwitchedPaused(
    user: number,
    paused: boolean,
): IPreferencesSwitchedPaused
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_SWITCHED_PAUSED,
        _target: user,
        paused,
        type: types.PREFERENCES_SWITCHED_PAUSED,
    };
}


/**
 * I create a preferences wallet reminder event.
 *
 * :param user: logged user id
 * :param walletReminder: wallet reminder display state
 *
 * :returns: preferences switched wallet reminder event
 */
function preferencesSwitchedWalletReminder(
    user: number,
    walletReminder: boolean,
): IPreferencesSwitchedWalletReminder
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_SWITCHED_WALLET_REMINDER,
        _target: user,
        type: types.PREFERENCES_SWITCHED_WALLET_REMINDER,
        walletReminder,
    };
}


/**
 * I create a preferences updated tag color event.
 *
 * :param user: logged user id
 *
 * :returns: preferences started event
 */
function preferencesTagColorSet(
    color: string,
    tag: number,
    user: number,
): IPreferencesTagColorSet
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_TAG_COLOR_SET,
        _target: user,
        color,
        tag,
        type: types.PREFERENCES_TAG_COLOR_SET,
    };
}


/**
 * I create a preferences tour disabled event.
 *
 * :param user: logged user id
 *
 * :returns: preferences tour disabled event
 */
function preferencesTourDisabled(
    user: number,
): IPreferencesTourDisabled
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: types.PREFERENCES_TOUR_DISABLED,
        _target: user,
        type: types.PREFERENCES_TOUR_DISABLED,
    };
}


/**
 * EXPORTS
 */
export {
    preferencesAddedAutoMessage,
    preferencesEmailPageSizeSet,
    preferencesFeedbackSet,
    preferencesRemovedAutoMessage,
    preferencesSetCurrentAutoMessage,
    preferencesStarted,
    preferencesSwitchedMuteNotifications,
    preferencesSwitchedPaused,
    preferencesSwitchedWalletReminder,
    preferencesTagColorSet,
    preferencesTourDisabled,
};
